import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db, storage } from "../config/firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import { Trash2, Loader, Edit2, Plus, X } from "lucide-react";
import { hasPermission, PERMISSIONS } from "../utils/roleAccess";

export default function ManageNotices() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [notices, setNotices] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  const [saveState, setSaveState] = useState({
    saving: false,
    success: "",
    error: "",
  });

  // Form state for adding/editing
  const [showForm, setShowForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    fileType: "text", // text, image, pdf
    file: null,
  });

  // Auth check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        const isAdminOrPrincipal =
          userSnap.exists() &&
          hasPermission(userSnap.data(), PERMISSIONS.MANAGE_NOTICES);
        setIsAdmin(isAdminOrPrincipal);

        if (!isAdminOrPrincipal) {
          navigate("/");
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        navigate("/");
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  // Load notices
  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "notices"));
        const noticesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNotices(noticesData);
      } catch (error) {
        console.error("Error fetching notices:", error);
        setNotices([]);
      }
      setDataLoading(false);
    };

    fetchNotices();
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        file,
      }));
    }
  };

  const handleAddNotice = () => {
    setEditingNotice(null);
    setFormData({
      title: "",
      description: "",
      fileType: "text",
      file: null,
    });
    setShowForm(true);
  };

  const handleEditNotice = (notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      description: notice.description,
      fileType: notice.fileType,
      file: null,
    });
    setShowForm(true);
  };

  const handleSaveNotice = async () => {
    if (!formData.title.trim()) {
      setSaveState({
        saving: false,
        success: "",
        error: "Title is required.",
      });
      return;
    }

    setSaveState({ saving: true, success: "", error: "" });

    try {
      let imageUrl = editingNotice?.imageUrl || "";

      // Upload file if provided
      if (formData.file) {
        const safeFileName = formData.file.name.replace(
          /[^a-zA-Z0-9._-]/g,
          "_",
        );
        const filePath = `notices/${formData.fileType}/${Date.now()}-${safeFileName}`;
        const fileRef = storageRef(storage, filePath);

        await uploadBytes(fileRef, formData.file);
        imageUrl = await getDownloadURL(fileRef);

        // Delete old file if exists
        if (
          editingNotice?.imageUrl &&
          editingNotice.imageUrl.includes("storage.googleapis.com")
        ) {
          try {
            const oldFileRef = storageRef(storage, editingNotice.imageUrl);
            await deleteObject(oldFileRef);
          } catch (err) {
            console.warn("Could not delete old file:", err);
          }
        }
      }

      const noticeData = {
        title: formData.title,
        description: formData.description,
        fileType: formData.fileType,
        imageUrl,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid,
      };

      if (editingNotice) {
        // Update existing
        await updateDoc(doc(db, "notices", editingNotice.id), noticeData);
        setNotices((prev) =>
          prev.map((n) =>
            n.id === editingNotice.id
              ? { ...n, ...noticeData, id: editingNotice.id }
              : n,
          ),
        );
        setSaveState({
          saving: false,
          success: "Notice updated successfully!",
          error: "",
        });
      } else {
        // Add new
        const docRef = await addDoc(collection(db, "notices"), {
          ...noticeData,
          createdAt: serverTimestamp(),
          createdBy: user?.uid,
        });
        setNotices((prev) => [
          ...prev,
          { id: docRef.id, ...noticeData, createdAt: new Date() },
        ]);
        setSaveState({
          saving: false,
          success: "Notice created successfully!",
          error: "",
        });
      }

      setShowForm(false);
      setFormData({
        title: "",
        description: "",
        fileType: "text",
        file: null,
      });
    } catch (error) {
      console.error("Save error:", error);
      setSaveState({
        saving: false,
        success: "",
        error: "Failed to save notice. Please try again.",
      });
    }
  };

  const handleDeleteNotice = async (noticeId, imageUrl) => {
    if (!window.confirm("Are you sure you want to delete this notice?")) {
      return;
    }

    setSaveState({ saving: true, success: "", error: "" });

    try {
      // Delete file from storage if exists
      if (imageUrl && imageUrl.includes("storage.googleapis.com")) {
        try {
          const fileRef = storageRef(storage, imageUrl);
          await deleteObject(fileRef);
        } catch (err) {
          console.warn("Failed to delete file:", err);
        }
      }

      // Delete from Firestore
      await deleteDoc(doc(db, "notices", noticeId));
      setNotices((prev) => prev.filter((n) => n.id !== noticeId));

      setSaveState({
        saving: false,
        success: "Notice deleted successfully!",
        error: "",
      });
    } catch (error) {
      console.error("Delete error:", error);
      setSaveState({
        saving: false,
        success: "",
        error: "Failed to delete notice. Please try again.",
      });
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="animate-spin" size={40} />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">
          You do not have permission to access this page.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Manage Notices & Updates
            </h1>
            <p className="text-gray-600">
              Create, edit, and delete notices displayed in the Updates
              component.
            </p>
          </div>
          <button
            onClick={handleAddNotice}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={20} />
            Add Notice
          </button>
        </div>

        {/* Status Messages */}
        {saveState.success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {saveState.success}
          </div>
        )}
        {saveState.error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {saveState.error}
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">
                  {editingNotice ? "Edit Notice" : "Add New Notice"}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="Notice title"
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="Notice description"
                    rows="3"
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Type
                  </label>
                  <select
                    name="fileType"
                    value={formData.fileType}
                    onChange={handleFormChange}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="text">Text Only</option>
                    <option value="image">Image</option>
                    <option value="pdf">PDF</option>
                  </select>
                </div>

                {formData.fileType !== "text" && (
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      {formData.fileType === "image" ? "Image" : "PDF"} File
                    </label>
                    <input
                      type="file"
                      accept={
                        formData.fileType === "image"
                          ? "image/*"
                          : "application/pdf"
                      }
                      onChange={handleFileSelect}
                      className="w-full p-2 border rounded-lg"
                    />
                    {formData.file && (
                      <p className="text-sm text-green-600 mt-2">
                        ✓ {formData.file.name}
                      </p>
                    )}
                    {editingNotice?.imageUrl && !formData.file && (
                      <p className="text-sm text-gray-600 mt-2">
                        Current file will be kept if not replaced
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowForm(false)}
                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNotice}
                    disabled={saveState.saving}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    {saveState.saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notices List */}
        <div className="bg-white rounded-lg shadow">
          {dataLoading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader className="animate-spin" size={40} />
            </div>
          ) : notices.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg">No notices yet.</p>
              <button
                onClick={handleAddNotice}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Create First Notice
              </button>
            </div>
          ) : (
            <div className="divide-y">
              {notices.map((notice) => (
                <div
                  key={notice.id}
                  className="p-6 hover:bg-gray-50 transition"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {notice.title}
                        </h3>
                        {notice.fileType === "image" && (
                          <span className="inline-block bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold">
                            Image
                          </span>
                        )}
                        {notice.fileType === "pdf" && (
                          <span className="inline-block bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-semibold">
                            PDF
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-3">
                        {notice.description || "No description"}
                      </p>
                      {notice.imageUrl && notice.fileType === "image" && (
                        <img
                          src={notice.imageUrl}
                          alt={notice.title}
                          className="max-w-[200px] max-h-[150px] object-cover rounded-lg mb-3"
                        />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditNotice(notice)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                        title="Edit"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteNotice(notice.id, notice.imageUrl)
                        }
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                        title="Delete"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
